import { useState } from "react";
import { motion } from "framer-motion";

// ---------------------------------------------------------------------------
// Real screenshots for the wireframe tour.
//
// Key = the step's `frame` value, e.g. "gap-assessment", "policy-store".
// The one exception is "form-generic", which is shared by ROPA / PIA / TPRM —
// for that frame the key is `form-generic:${frameLabel}` so each form gets
// its own screenshot.
//
// Drop the screenshot files in your app's `public/tour/` folder (any path
// works as long as it matches what's below). If a key has no entry, or the
// image fails to load, MockPageFrame automatically falls back to the
// hand-drawn wireframe below — so nothing breaks while you're still
// collecting screenshots.
// ---------------------------------------------------------------------------
const SCREENSHOTS = {
  "welcome:User": { src: "/tour/navbar.jpg", ratio: 1600 / 64 },
  "welcome:Tenant Admin": { src: "/tour/navbar-tenant.jpg", ratio: 1600 / 64 },
  "gap-assessment": { src: "/tour/gap-assessment.jpg", ratio: 1600 / 727 },
  "gap-assessment-submit": { src: "/tour/gap-assessment.jpg", ratio: 1600 / 727 },
  "form-generic:ROPA": { src: "/tour/form-generic-ROPA.jpg", ratio: 1600 / 730 },
  "form-generic:PIA": { src: "/tour/form-generic-PIA.jpg", ratio: 1600 / 732 },
  "form-generic:TPRM": { src: "/tour/form-generic-TPRM.jpg", ratio: 1600 / 721 },
  "policy-store": { src: "/tour/policy-store.jpg", ratio: 1600 / 728 },
  "user-home": { src: "/tour/user-home.jpg", ratio: 1600 / 1565 },
  "breach-management": { src: "/tour/breach-management.jpg", ratio: 1600 / 2150 },
  "user-dashboard": { src: "/tour/user-dashboard.jpg", ratio: 1600 / 1398 },
  reports: { src: "/tour/reports.jpg", ratio: 1213 / 1588 },
  "login:Admin": { src: "/tour/login-admin.jpg", ratio: 1600 / 732 },
  "login:Tenant Admin": { src: "/tour/login-tenant.jpg", ratio: 1600 / 724 },
  "login:User": { src: "/tour/login-user.jpg", ratio: 1600 / 725 },
  journey: { src: "/tour/journey.svg", ratio: 1600 / 640 },

  // Tenant Admin
  "tenant-dashboard": { src: "/tour/tenant-dashboard.jpg", ratio: 1600 / 960 },
  "tenant-users": { src: "/tour/tenant-users.jpg", ratio: 1600 / 838 },
  departments: { src: "/tour/departments.jpg", ratio: 1600 / 732 },
  "gap-admin": { src: "/tour/gap-admin.jpg", ratio: 1600 / 731 },
  "form-questions": { src: "/tour/form-questions.jpg", ratio: 1600 / 729 },
  "settings-tenant": { src: "/tour/settings-tenant.jpg", ratio: 1600 / 2347 },
  license: { src: "/tour/license.jpg", ratio: 1600 / 902 },
  "email-templates": { src: "/tour/email-templates.jpg", ratio: 1600 / 1307 },
  "user-responses:Gap Assessment": { src: "/tour/user-responses-Gap-Assessment.jpg", ratio: 1600 / 740 },
  "user-responses:ROPA": { src: "/tour/user-responses-ROPA.jpg", ratio: 1600 / 753 },
  "user-responses:PIA": { src: "/tour/user-responses-PIA.jpg", ratio: 1600 / 734 },
  faq: { src: "/tour/faq.svg", ratio: 1600 / 680 },
  roles: { src: "/tour/roles.svg", ratio: 1600 / 760 },
};

// Frames shared by multiple pages (form-generic → ROPA/PIA/TPRM, login →
// Admin/Tenant Admin/User) are keyed as `${frame}:${frameLabel}` so each
// variant gets its own screenshot. Frames with only one screenshot (e.g.
// "policy-store") are keyed by `frame` alone.
function screenshotKey(frame, frameLabel) {
  const labeledKey = frameLabel ? `${frame}:${frameLabel}` : null;
  if (labeledKey && SCREENSHOTS[labeledKey]) return labeledKey;
  return frame;
}

function MockNav({ highlightNav = null }) {
  const items = ["Home", "Dashboard", "Assessment ▾", "Reports", "Help"];
  return (
    <div
      className="flex items-center gap-1 px-3 py-2 rounded-t-lg"
      style={{ background: "linear-gradient(160deg, #0a1628 0%, #112366 100%)" }}
    >
      <div className="flex items-center gap-1.5 mr-3">
        <div className="w-6 h-6 rounded-md bg-gradient-to-br from-indigo-500 to-sky-400" />
        <span className="text-[10px] font-bold text-white">OneDPDP</span>
      </div>
      {items.map((item) => (
        <div
          key={item}
          className={`px-2 py-1 rounded text-[8px] ${
            highlightNav === item ? "bg-white/20 text-white font-semibold" : "text-sky-200/70"
          }`}
        >
          {item}
        </div>
      ))}
      <div className="ml-auto w-5 h-5 rounded-full bg-indigo-400/40" />
    </div>
  );
}

function Skeleton({ className = "" }) {
  return <div className={`rounded bg-slate-200/80 animate-pulse ${className}`} />;
}

const FRAMES = {
  welcome: () => (
    <div className="h-full flex flex-col bg-slate-50">
      <MockNav />
      <div className="flex-1 p-4 space-y-3">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-4 w-full" />
        <div className="grid grid-cols-3 gap-2 mt-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-xl bg-white border border-slate-200 p-3 h-24">
              <Skeleton className="h-3 w-1/2 mb-2" />
              <Skeleton className="h-2 w-full" />
            </div>
          ))}
        </div>
      </div>
    </div>
  ),
  roles: () => (
    <div className="h-full p-4 bg-gradient-to-br from-slate-50 to-indigo-50/30">
      <div className="grid grid-cols-3 gap-2 h-full">
        {[
          { label: "Super Admin", color: "#6366f1" },
          { label: "Tenant Admin", color: "#0ea5e9" },
          { label: "End User", color: "#059669" },
        ].map((r) => (
          <div
            key={r.label}
            className="rounded-xl border-2 p-2"
            style={{ borderColor: `${r.color}40`, background: `${r.color}08` }}
          >
            <span className="text-[9px] font-bold" style={{ color: r.color }}>{r.label}</span>
            <Skeleton className="h-2 w-full mt-2 mb-1" />
            <Skeleton className="h-2 w-4/5" />
          </div>
        ))}
      </div>
    </div>
  ),
  login: () => (
    <div
      className="h-full flex items-center justify-center"
      style={{ background: "linear-gradient(135deg, #0a1628 0%, #112366 100%)" }}
    >
      <div className="w-[55%] bg-white/95 rounded-2xl p-5 shadow-xl">
        <Skeleton className="h-7 w-full mb-2 rounded-lg" />
        <Skeleton className="h-7 w-full mb-2 rounded-lg" />
        <Skeleton className="h-7 w-full mb-2 rounded-lg" />
        <div className="h-8 w-full rounded-lg bg-indigo-600 mt-3 flex items-center justify-center">
          <span className="text-[9px] text-white font-semibold">Sign In</span>
        </div>
      </div>
    </div>
  ),
  "user-home": () => (
    <div className="h-full flex flex-col bg-gradient-to-br from-slate-50 to-indigo-50/40">
      <MockNav highlightNav="Home" />
      <div className="flex-1 p-3 grid grid-cols-2 gap-2">
        <div className="rounded-xl bg-white/80 border p-2 flex items-center justify-center">
          <div className="w-16 h-16 rounded-full border-4 border-indigo-200" />
        </div>
        <div className="rounded-xl bg-white/80 border p-2 space-y-1">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-2 w-full" />)}
        </div>
      </div>
    </div>
  ),
  "user-dashboard": () => (
    <div className="h-full flex flex-col bg-slate-50">
      <MockNav highlightNav="Dashboard" />
      <div className="flex-1 p-3 grid grid-cols-4 gap-2">
        {["Consent", "DSAR", "DSPM", "Forms"].map((label, i) => (
          <div key={label} className="rounded-xl bg-white border p-2 h-16">
            <span className="text-[7px] text-slate-500">{label}</span>
            <div className="text-sm font-bold text-indigo-600">{[847, 23, 156, 72][i]}</div>
          </div>
        ))}
      </div>
    </div>
  ),
  "tenant-dashboard": () => (
    <div className="h-full flex flex-col bg-slate-50">
      <MockNav />
      <div className="flex-1 p-3 grid grid-cols-3 gap-2">
        {["Users", "License", "Actions"].map((t) => (
          <div key={t} className="rounded-xl bg-white border p-2"><Skeleton className="h-10 w-full" /></div>
        ))}
      </div>
    </div>
  ),
  "admin-dashboard": () => (
    <div className="h-full flex flex-col bg-slate-50">
      <MockNav />
      <div className="flex-1 p-3 grid grid-cols-3 gap-2">
        {["Tenants", "Users", "Licenses"].map((t) => (
          <div key={t} className="rounded-xl bg-white border p-2"><Skeleton className="h-10 w-full" /></div>
        ))}
      </div>
    </div>
  ),
  "gap-assessment": () => (
    <div className="h-full flex flex-col bg-white">
      <MockNav highlightNav="Assessment ▾" />
      <div className="flex-1 flex">
        <div className="w-[22%] border-r bg-slate-50 p-2 space-y-1">
          {["Governance", "Security", "Rights"].map((s, i) => (
            <div key={s} className={`px-2 py-1 rounded text-[8px] ${i === 0 ? "bg-indigo-100 text-indigo-700" : "text-slate-500"}`}>{s}</div>
          ))}
        </div>
        <div className="flex-1 p-3 space-y-2">
          <Skeleton className="h-6 w-full rounded-lg" />
          <Skeleton className="h-6 w-full rounded-lg" />
        </div>
      </div>
    </div>
  ),
  "gap-assessment-submit": () => (
    <div className="h-full flex flex-col bg-white">
      <MockNav />
      <div className="flex-1 p-3"><Skeleton className="h-24 w-full rounded-xl" /></div>
      <div className="border-t p-2 flex justify-between bg-slate-50">
        <span className="text-[8px] text-indigo-600">Submissions</span>
        <div className="flex gap-1">
          <div className="px-2 py-1 rounded border text-[8px]">Save Draft</div>
          <div className="px-2 py-1 rounded bg-indigo-600 text-[8px] text-white">Submit</div>
        </div>
      </div>
    </div>
  ),
  "form-generic": ({ label = "Form", color = "#6366f1" }) => (
    <div className="h-full flex flex-col bg-white">
      <MockNav highlightNav="Assessment ▾" />
      <div className="flex-1 p-3">
        <span className="text-[10px] font-bold px-2 py-1 rounded" style={{ background: `${color}15`, color }}>{label}</span>
        <Skeleton className="h-8 w-full mt-3 rounded-lg mb-2" />
        <Skeleton className="h-8 w-full rounded-lg" />
      </div>
      <div className="border-t p-2 flex justify-end bg-slate-50">
        <div className="px-3 py-1 rounded bg-indigo-600 text-[8px] text-white">Submit</div>
      </div>
    </div>
  ),
  "breach-management": () => (
    <div className="h-full flex flex-col bg-slate-50">
      <MockNav />
      <div className="flex-1 p-3">
        <div className="flex justify-end mb-2">
          <div className="px-2 py-1 rounded bg-red-600 text-[8px] text-white">+ Report Incident</div>
        </div>
        <div className="rounded-xl bg-white border p-3 space-y-2">
          <Skeleton className="h-6 w-full rounded" />
          <Skeleton className="h-12 w-full rounded" />
        </div>
      </div>
    </div>
  ),
  "consent-variance": () => (
    <div className="h-full flex flex-col bg-slate-50">
      <MockNav />
      <div className="flex-1 p-3">
        <div className="rounded-xl bg-white border overflow-hidden text-[7px]">
          <div className="grid grid-cols-4 bg-slate-100 p-1.5 font-semibold">
            {["Type", "Consent", "DSPM", "Status"].map((h) => <div key={h}>{h}</div>)}
          </div>
          <div className="grid grid-cols-4 p-1.5 border-t">
            <div>Email</div><div>✓</div><div>✓</div><div className="text-red-600">Mismatch</div>
          </div>
        </div>
      </div>
    </div>
  ),
  "policy-store": () => (
    <div className="h-full flex flex-col bg-slate-50">
      <MockNav />
      <div className="flex-1 flex p-2 gap-2">
        <div className="w-[30%] space-y-1">
          {["Privacy Notice", "Retention"].map((p, i) => (
            <div key={p} className={`p-2 rounded text-[8px] ${i === 0 ? "bg-indigo-100 text-indigo-700" : "bg-white border"}`}>{p}</div>
          ))}
        </div>
        <div className="flex-1 bg-white rounded-xl border p-2">
          <Skeleton className="h-2 w-full mb-1" />
          <Skeleton className="h-2 w-4/5" />
        </div>
      </div>
    </div>
  ),
  reports: () => (
    <div className="h-full flex flex-col bg-slate-50">
      <MockNav highlightNav="Reports" />
      <div className="flex-1 flex p-2 gap-2">
        <div className="w-[28%] space-y-1">
          {["Executive", "Detailed"].map((t, i) => (
            <div key={t} className={`p-2 rounded text-[8px] ${i === 0 ? "bg-indigo-600 text-white" : "bg-white border"}`}>{t}</div>
          ))}
        </div>
        <div className="flex-1 bg-white rounded-xl border p-2 flex flex-col">
          <Skeleton className="h-16 w-full rounded" />
          <div className="mt-auto self-end px-3 py-1 rounded bg-indigo-600 text-[8px] text-white">Generate PDF</div>
        </div>
      </div>
    </div>
  ),
  "tenant-users": () => (
    <div className="h-full flex flex-col bg-slate-50">
      <MockNav />
      <div className="flex-1 p-3">
        <div className="flex justify-end mb-2">
          <div className="px-2 py-1 rounded bg-indigo-600 text-[8px] text-white">+ Create User</div>
        </div>
        <div className="rounded-xl bg-white border p-2 text-[8px] space-y-1">
          <div className="flex justify-between"><span>User 1</span><span className="text-indigo-500">✎</span></div>
          <div className="flex justify-between border-t pt-1"><span>User 2</span><span className="text-indigo-500">✎</span></div>
        </div>
      </div>
    </div>
  ),
  departments: () => (
    <div className="h-full flex flex-col bg-slate-50">
      <MockNav />
      <div className="flex-1 p-3 grid grid-cols-3 gap-2">
        {["HR", "IT", "Legal"].map((d) => (
          <div key={d} className="rounded-xl bg-white border p-2 text-[9px] font-semibold">{d}</div>
        ))}
      </div>
    </div>
  ),
  "gap-admin": () => (
    <div className="h-full flex flex-col bg-slate-50">
      <MockNav />
      <div className="flex-1 p-3">
        <div className="flex gap-1 mb-2">
          {["Overview", "Cycles", "Gaps"].map((t, i) => (
            <div key={t} className={`px-2 py-0.5 rounded text-[7px] ${i === 0 ? "bg-indigo-600 text-white" : "bg-white border"}`}>{t}</div>
          ))}
        </div>
        <div className="rounded-xl bg-white border p-2 h-24 flex items-end gap-1">
          {[60, 80, 45].map((h, i) => (
            <div key={i} className="flex-1 rounded-t bg-indigo-400/60" style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>
    </div>
  ),
  "form-questions": () => (
    <div className="h-full flex flex-col bg-slate-50">
      <MockNav />
      <div className="flex-1 p-3 space-y-1">
        <div className="flex gap-1 mb-1">
          {["Gap", "ROPA", "PIA"].map((t, i) => (
            <div key={t} className={`px-2 py-0.5 rounded text-[7px] ${i === 0 ? "bg-indigo-600 text-white" : "border bg-white"}`}>{t}</div>
          ))}
        </div>
        {[1, 2, 3].map((q) => <div key={q} className="rounded bg-white border p-2"><Skeleton className="h-2 w-3/4" /></div>)}
      </div>
    </div>
  ),
  "admin-tenants": () => (
    <div className="h-full flex flex-col bg-slate-50">
      <MockNav />
      <div className="flex-1 p-3">
        <div className="flex justify-end mb-2">
          <div className="px-2 py-1 rounded bg-indigo-600 text-[8px] text-white">+ Add Tenant</div>
        </div>
        <div className="rounded-xl bg-white border divide-y text-[8px]">
          {["Acme", "TechCorp"].map((t) => <div key={t} className="p-2">{t}</div>)}
        </div>
      </div>
    </div>
  ),
  settings: () => (
    <div className="h-full flex flex-col bg-slate-50">
      <MockNav />
      <div className="flex-1 p-3 grid grid-cols-2 gap-2">
        <div className="rounded-xl bg-white border p-2 text-[8px]">SMTP</div>
        <div className="rounded-xl bg-white border p-2 text-[8px]">Templates</div>
      </div>
    </div>
  ),
  journey: () => (
    <div className="h-full p-4 flex items-center bg-gradient-to-r from-indigo-50 to-sky-50">
      <div className="flex w-full justify-between">
        {[1, 2, 3, 4, 5, 6].map((n) => (
          <div key={n} className="flex flex-col items-center">
            <div className="w-7 h-7 rounded-full bg-indigo-600 text-white text-[9px] flex items-center justify-center font-bold">{n}</div>
          </div>
        ))}
      </div>
    </div>
  ),
  faq: () => (
    <div className="h-full p-3 grid grid-cols-2 gap-2 bg-slate-50">
      {["Login issues", "Email fails", "License limit", "Submit blocked"].map((t) => (
        <div key={t} className="rounded-xl bg-white border p-2 text-[9px] font-medium">{t}</div>
      ))}
    </div>
  ),
};

export default function MockPageFrame({ frame, frameLabel, frameColor, className = "" }) {
  const [imgFailed, setImgFailed] = useState(false);

  const key = screenshotKey(frame, frameLabel);
  const screenshot = SCREENSHOTS[key];
  const showScreenshot = Boolean(screenshot) && !imgFailed;

  const Renderer = FRAMES[frame] || FRAMES.welcome;
  const props = frame === "form-generic" ? { label: frameLabel, color: frameColor } : {};

  // Wireframe frames are always drawn in a fixed 16:10 box. Real screenshots
  // use their own true aspect ratio instead — forcing them into 16:10 with
  // object-cover crops the page and throws off every hotspot's x/y/w/h
  // percentage (which is measured against the real, uncropped screenshot).
  const aspectRatio = showScreenshot ? screenshot.ratio : 16 / 10;

  return (
    <div
      className={`relative overflow-hidden rounded-xl border border-slate-300/80 shadow-lg bg-white ${className}`}
      style={{ aspectRatio }}
    >
      <div className="absolute top-2 right-2 z-10 px-2 py-0.5 rounded-full bg-slate-800/70 text-[8px] text-slate-300">
        {showScreenshot ? "Live Screenshot" : "Preview"}
      </div>
      {showScreenshot ? (
        <img
          src={screenshot.src}
          alt={frameLabel || frame}
          className="w-full h-full block"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <Renderer {...props} />
      )}
    </div>
  );
}

export function TourCursor({ x, y, visible = true }) {
  if (!visible) return null;
  return (
    <motion.div
      className="absolute z-30 pointer-events-none"
      style={{ left: `${x}%`, top: `${y}%`, transform: "translate(-20%, -10%)" }}
      animate={{ scale: [1, 1.15, 1] }}
      transition={{ duration: 1.2, repeat: Infinity }}
    >
      <svg width="24" height="24" viewBox="0 0 24 24">
        <path d="M5 3L5 18L9.5 13.5L13 21L16 19.5L12.5 12L18 11L5 3Z" fill="#6366f1" stroke="white" strokeWidth="1.5" />
      </svg>
    </motion.div>
  );
}

export function TourHotspot({ hotspot, index, isActive, isHovered, onHover, onClick }) {
  const { x, y, w, h, label, tooltip } = hotspot;
  return (
    <motion.button
      type="button"
      className="absolute z-20 cursor-pointer"
      style={{ left: `${x}%`, top: `${y}%`, width: `${w}%`, height: `${h}%` }}
      onMouseEnter={() => onHover(hotspot.id)}
      onMouseLeave={() => onHover(null)}
      onClick={() => onClick(hotspot.id)}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: index * 0.08 }}
    >
      <div className={`absolute inset-0 rounded-lg border-2 ${isActive ? "border-indigo-500 bg-indigo-500/20 shadow-lg" : isHovered ? "border-sky-400 bg-sky-400/15" : "border-amber-400/80 bg-amber-400/10"}`} />
      {isActive && (
        <motion.div className="absolute inset-0 rounded-lg border-2 border-indigo-400" animate={{ scale: [1, 1.08, 1], opacity: [0.8, 0.3, 0.8] }} transition={{ duration: 1.5, repeat: Infinity }} />
      )}
      <div className={`absolute -top-2 -left-2 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${isActive ? "bg-indigo-600" : "bg-amber-500"}`}>
        {index + 1}
      </div>
      {(isHovered || isActive) && (
        <div className="absolute left-1/2 -translate-x-1/2 -top-9 z-40 whitespace-nowrap px-2 py-1 rounded-lg bg-slate-900 text-white text-[10px] shadow-xl pointer-events-none">
          {tooltip || label}
        </div>
      )}
    </motion.button>
  );
}
